import { setOnwardSelectedSort, setReturnSelectedSort } from '@/src/redux/slices/srpStatesSlice'
import { useAppDispatch } from '@/src/redux/store'
import { FlightCard, SortDataArrays } from '@/src/types/flight'
import { useTranslations } from 'next-intl'
import { useCallback, useMemo } from 'react'
import useClevertapForSrp from '../../analytics/clevertap/srp'
import useFormatCurrencyForSrp from '../../currency/useFormatCurrencyForSrp'
import { useFormatTime } from '../formatTime'
import useSearchResults from '../useSearchResults'

interface SortOption {
    id: string
    label: string
    priceAndDuration: string
    sortKey: string
    isActive?: boolean
    card?: FlightCard
    isCombined?: boolean
}

const trackSortEvent = (sortKey: string, triggerSrpEvent: (name: string, payload: any) => void) => {
    const actionTypeMap: Record<string, string> = {
        price_up: 'Cheapest',
        duration_up: 'fastest',
        departure_up: 'earlyMorning',
        departure_down: 'lateNight'
    }

    const actionValueMap: Record<string, string> = {
        price_up: 'sort_ascending',
        duration_up: 'sort_ascending',
        departure_up: 'sort_ascending',
        departure_down: 'sort_descending'
    }

    triggerSrpEvent('Air_UI_Action', {
        action_name: 'sort',
        action_type: actionTypeMap[sortKey] || sortKey,
        action_value: actionValueMap[sortKey] || 'sort_ascending'
    })
}

const getCardForTargetedSort = (sortIds: string[], selectedLegSortedFilteredCards: FlightCard[]) => {
    for (const id of sortIds) {
        const card = selectedLegSortedFilteredCards.find(c => c.id === id)
        if (card) {
            return card
        }
    }
    return undefined
}

export const useSortManager = () => {
    const {
        selectedSortForSelectedJourneyLeg,
        sortData,
        selectedJourneyLeg,
        shouldShowMultiAirlinePrices,
        selectedLegSortedFilteredCards,
        isPerPaxPricingEnabled
    } = useSearchResults()
    const { triggerSrpEvent } = useClevertapForSrp()
    const formatTime = useFormatTime()
    const { formatCurrency } = useFormatCurrencyForSrp()
    const dispatch = useAppDispatch()
    const t = useTranslations('common')

    const getFormattedPriceAndDurationLabel = useCallback(
        (card?: FlightCard) => {
            return {
                priceAndDuration: card
                    ? `${formatCurrency(card.priceBreakup?.[shouldShowMultiAirlinePrices ? 'multiAirlinePrice' : isPerPaxPricingEnabled ? 'totalAdultFare' : 'pr'] ?? 0)} • ${formatTime(card?.totalDuration)}`
                    : '',
                card: card
            }
        },
        [formatCurrency, formatTime, shouldShowMultiAirlinePrices, isPerPaxPricingEnabled]
    )

    const sortOptions: SortOption[] = useMemo(
        () => [
            {
                id: 'cheapest',
                label: `${t('cheapest')}`,
                sortKey: 'price_up',
                isActive: selectedSortForSelectedJourneyLeg?.sortKey === 'price_up',
                ...getFormattedPriceAndDurationLabel(
                    getCardForTargetedSort(
                        sortData?.price_up?.[selectedJourneyLeg] || [],
                        selectedLegSortedFilteredCards
                    )
                )
            },
            {
                id: 'quickest',
                label: `${t('quickest')}`,
                sortKey: 'duration_up',
                isActive: selectedSortForSelectedJourneyLeg?.sortKey === 'duration_up',
                ...getFormattedPriceAndDurationLabel(
                    getCardForTargetedSort(
                        sortData?.duration_up?.[selectedJourneyLeg] || [],
                        selectedLegSortedFilteredCards
                    )
                )
            },
            {
                id: 'earlyMorning',
                label: `${t('earlyMorning')}`,
                sortKey: 'departure_up',
                isActive: selectedSortForSelectedJourneyLeg?.sortKey === 'departure_up',
                ...getFormattedPriceAndDurationLabel(
                    getCardForTargetedSort(
                        sortData?.departure_up?.[selectedJourneyLeg] || [],
                        selectedLegSortedFilteredCards
                    )
                )
            },
            {
                id: 'lateNight',
                label: `${t('lateNight')}`,
                sortKey: 'departure_down',
                isActive: selectedSortForSelectedJourneyLeg?.sortKey === 'departure_down',
                ...getFormattedPriceAndDurationLabel(
                    getCardForTargetedSort(
                        sortData?.departure_down?.[selectedJourneyLeg] || [],
                        selectedLegSortedFilteredCards
                    )
                )
            }
        ],
        [
            selectedSortForSelectedJourneyLeg,
            sortData,
            selectedJourneyLeg,
            shouldShowMultiAirlinePrices,
            getFormattedPriceAndDurationLabel,
            selectedLegSortedFilteredCards
        ]
    )

    const handleSortSelect = useCallback(
        (sortKey: string) => {
            const option = sortOptions.find(opt => opt.sortKey === sortKey)
            if (option) {
                trackSortEvent(sortKey, triggerSrpEvent)
                selectedJourneyLeg === 0
                    ? dispatch(
                        setOnwardSelectedSort({
                            sortKey: option.sortKey as keyof SortDataArrays,
                            displayValue: option.label
                        })
                    )
                    : dispatch(
                        setReturnSelectedSort({
                            sortKey: option.sortKey as keyof SortDataArrays,
                            displayValue: option.label
                        })
                    )
            }
        },
        [sortOptions, triggerSrpEvent, selectedJourneyLeg, dispatch]
    )

    return {
        sortOptions,
        handleSortSelect
    }
}
